import { useLocation } from "@tanstack/react-router";
import type { ClientMaintenanceStatus } from "api-types/maintenance/maintenanceResponses";
import { getMaintenanceStatusURL } from "lib/endpointHelpers";
import { useFetchNoAuth } from "lib/fetcher";
import { userIsSystemAdmin } from "lib/security/shared";
import { useUserContext } from "lib/state/UserContextProvider";
import type { ReactNode } from "react";
import MaintenancePage from "./MaintenancePage";

// Poll so the gate appears/disappears without a manual reload when an admin toggles it.
const POLL_INTERVAL_MS = 60_000;

// Auth routes stay reachable during maintenance so a signed-out admin can still log in (and
// complete SSO) to turn it off. These pages have no gated app functionality of their own.
function isAuthRoute(pathname: string): boolean {
  return pathname.startsWith("/account/");
}

// The gate never runs inside an embed (iframe) — e.g. the Showroom Intro widget on a builder's
// own website. A full-screen Higharc maintenance takeover would be off-brand there, and the
// embed is intentionally limited to a minimal set of unauthenticated API calls (so we also skip
// the status poll). The top-level Higharc app still gates normally.
function isEmbedded(): boolean {
  return typeof window !== "undefined" && window.self !== window.top;
}

interface MaintenanceGateProps {
  children: ReactNode;
}

/**
 * Renders a full-screen maintenance page for every user when maintenance mode is enabled.
 * The status endpoint is unauthenticated, so this works before login. Until the first fetch
 * resolves, children render normally to avoid a flash-of-block.
 *
 * System-admins are exempt: they keep full app access so they can reach Admin → Settings to
 * turn maintenance off (and verify the system) while it's on. Everyone else — logged-out
 * users, non-admins, and super-admins — sees the page. The write-block, if enabled, still
 * applies to everyone at the DB layer regardless of this exemption.
 */
export default function MaintenanceGate({ children }: MaintenanceGateProps) {
  const { pathname } = useLocation();
  const { user, userLoading } = useUserContext();
  // Empty key skips the fetch (see createFetchKey) so embeds make no maintenance-status call.
  const { data } = useFetchNoAuth<ClientMaintenanceStatus>(isEmbedded() ? "" : getMaintenanceStatusURL(), [], {
    refreshInterval: POLL_INTERVAL_MS,
  });

  const isSystemAdmin = userIsSystemAdmin(user ?? undefined, true);

  if (data?.enabled && !isSystemAdmin && !isAuthRoute(pathname)) {
    // Maintenance is on and this viewer isn't exempt. Wait for the user to resolve before
    // committing: rendering children while `/users/me` is in flight would (a) flash the page
    // for a system-admin who's still loading and (b) let an authed route redirect a logged-out
    // user to login instead of showing the page. Logged-out contexts report `userLoading`
    // falsy, so they gate immediately without a blank frame.
    if (userLoading) {
      return null;
    }
    return <MaintenancePage endTime={data.endTime} message={data.message} />;
  }

  return <>{children}</>;
}
